import React, { useCallback, useState } from 'react';
import classNames from 'classnames';
import { compose } from '../../node_module_clones/recompose';
import { useSelector } from 'react-redux';

import SelectionMarquee from '../canvas/selection/SelectionMarquee';
import OrchestratedList from '../list/OrchestratedList';
import List from '../list/List';
import EmptyInboxPlaceholder from './EmptyInboxPlaceholder';

import markSeenInboxPointersDecorator from '../components/pointsOfInterest/pointers/markSeenInboxPointersDecorator';
import selectionMarqueeCanvasStateDecorator from '../canvas/selection/selectionMarqueeCanvasStateDecorator';
import { zoomStateContextConsumer } from '../canvas/zoom/ZoomStateContext';
import { isPlatformIpad } from '../platform/utils/platformDetailsUtils';
import { getPlatformDetailsSelector } from '../platform/platformSelector';
import useElementListPlaceholdersInitialisation from '../element/elementPlaceholder/useElementListPlaceholdersInitialisation';

import inboxCanDrop from './inboxCanDrop';

import { ELEMENT_DEFAULT_WIDTH } from '../../common/elements/elementConstants';
import { IN_LIST_MODE } from '../../common/inList/listTypes';

import './InboxScrollableList.scss';

type InboxScrollableListProps = {
    gridSize: number;
    isEditable: boolean;
    isHovered: boolean;
    inboxElementIds: string[];
    currentBoardId: string;
    selectionMarqueeActive: boolean;
    setSelectionMarqueeActive: (active: boolean) => void;
    listElementProps: any;
    defaultFirstBatchSize?: number;
    listZoomScale?: number;
    getContextZoomScale: () => number;
    listContainerRef: React.MutableRefObject<HTMLDivElement | null>;
    cleanupOnOrchestrationEnd?: () => void;
};

const MARQUEE_OFFSET = { x: 0, y: -5 };
const INITIAL_RENDER_SIZE = 20;

const enhance = compose(markSeenInboxPointersDecorator, selectionMarqueeCanvasStateDecorator, zoomStateContextConsumer);

const InboxScrollableList = (props: InboxScrollableListProps) => {
    const {
        gridSize,
        isEditable,
        inboxElementIds,
        currentBoardId,
        selectionMarqueeActive,
        setSelectionMarqueeActive,
        listElementProps,
        defaultFirstBatchSize = INITIAL_RENDER_SIZE,
        listZoomScale,
        listContainerRef,
        cleanupOnOrchestrationEnd,
    } = props;

    const platformDetails = useSelector(getPlatformDetailsSelector);

    // We need to re-render when the ref updates due to ensure that the selection
    // marquee's list container ref is up to date and attaches events to the correct
    // DOM node
    const [listContainerNode, setListContainerNode] = useState(null);
    const listContainerRefCallback = useCallback((node) => {
        if (node !== null) {
            listContainerRef.current = node;
            setListContainerNode(node);
        }
    }, []);

    const initialRenderSize = useElementListPlaceholdersInitialisation(inboxElementIds, defaultFirstBatchSize);

    return (
        <div
            className={classNames('InboxScrollableList', {
                'selection-marquee-active': selectionMarqueeActive,
            })}
            data-scrollable
        >
            <SelectionMarquee
                sectionElementIds={inboxElementIds}
                setSelectionMarqueeActive={setSelectionMarqueeActive}
                eventListenerNode={listContainerNode}
                offset={MARQUEE_OFFSET}
                containerWidth={gridSize * (ELEMENT_DEFAULT_WIDTH + 3.9)}
                gridSize={gridSize}
                disabled={!isEditable}
            />
            <OrchestratedList
                key={currentBoardId}
                childElementIds={inboxElementIds}
                onOrchestrationEnd={cleanupOnOrchestrationEnd}
                initialRenderSize={initialRenderSize}
            >
                {(childElementIds: string[], listSize: number) => {
                    return (
                        <List
                            {...props}
                            childElementIds={childElementIds}
                            listSize={listSize}
                            listId={currentBoardId}
                            listContainerRef={listContainerRefCallback}
                            placeholderComponent={<EmptyInboxPlaceholder {...props} />}
                            inListClass={IN_LIST_MODE.IN_LIST_UNSORTED_NOTES}
                            listCanDropFn={inboxCanDrop}
                            listElementProps={listElementProps}
                            listZoomScale={listZoomScale}
                        />
                    );
                }}
            </OrchestratedList>
            {isPlatformIpad(platformDetails) && <div className="ipad-virtual-keyboard-toolbar-inset"></div>}
        </div>
    );
};

// @ts-ignore-next-line The props are correct, just confused by untyped decorators
export default enhance(InboxScrollableList);
